import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail, Clock, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Eye,
      title: "1. Collecte des données personnelles",
      content: `Dans le cadre de notre activité, nous sommes amenés à collecter les données personnelles suivantes :

• **Données d'identification** : nom, prénom, adresse email, numéro de téléphone
• **Données professionnelles** : nom de l'entreprise, fonction, secteur d'activité
• **Données de navigation** : adresse IP, type de navigateur, pages visitées, durée de visite
• **Données de communication** : messages envoyés via le formulaire de contact

Ces données sont collectées lorsque vous :
- Remplissez notre formulaire de contact
- Demandez un devis
- Vous inscrivez à notre newsletter
- Naviguez sur notre site web`
    },
    {
      icon: Database,
      title: "2. Finalités du traitement",
      content: `Vos données personnelles sont collectées pour les finalités suivantes :

• **Gestion des demandes de contact** : répondre à vos questions et demandes de devis
• **Relation commerciale** : suivi de nos échanges et gestion de nos projets communs
• **Amélioration de nos services** : analyse statistique anonymisée de la fréquentation du site
• **Communication marketing** : envoi de newsletters (avec votre consentement explicite)
• **Obligations légales** : respect de nos obligations comptables et fiscales

Nous ne procédons à aucune prise de décision automatisée ni à aucun profilage.`
    },
    {
      icon: Lock,
      title: "3. Base légale du traitement",
      content: `Le traitement de vos données repose sur les bases légales suivantes :

• **Votre consentement** : pour l'envoi de communications marketing
• **L'exécution d'un contrat** : pour la gestion de nos prestations
• **Notre intérêt légitime** : pour répondre à vos demandes de contact et améliorer nos services
• **Nos obligations légales** : pour le respect de la réglementation en vigueur

Vous pouvez retirer votre consentement à tout moment en nous contactant.`
    },
    {
      icon: Clock,
      title: "4. Durée de conservation",
      content: `Vos données personnelles sont conservées pendant une durée limitée :

• **Données de contact** : 3 ans à compter du dernier échange
• **Données clients** : durée de la relation commerciale + 5 ans (obligations légales)
• **Données de navigation** : 13 mois maximum
• **Cookies** : 13 mois maximum

Au-delà de ces durées, vos données sont supprimées ou anonymisées.`
    },
    {
      icon: Shield,
      title: "5. Sécurité des données",
      content: `Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données :

• **Chiffrement SSL/TLS** : toutes les communications sont sécurisées
• **Accès restreint** : seules les personnes habilitées accèdent à vos données
• **Hébergement sécurisé** : nos serveurs sont hébergés en France/Europe
• **Sauvegardes régulières** : protection contre la perte de données
• **Mise à jour de sécurité** : nos systèmes sont régulièrement mis à jour

En cas de violation de données, nous vous informerons dans les meilleurs délais conformément au RGPD.`
    },
    {
      icon: FileText,
      title: "6. Vos droits",
      content: `Conformément au RGPD, vous disposez des droits suivants :

• **Droit d'accès** : obtenir la confirmation que vos données sont traitées et en recevoir une copie
• **Droit de rectification** : corriger vos données inexactes ou incomplètes
• **Droit à l'effacement** : demander la suppression de vos données ("droit à l'oubli")
• **Droit à la limitation** : limiter le traitement de vos données
• **Droit à la portabilité** : recevoir vos données dans un format structuré
• **Droit d'opposition** : vous opposer au traitement de vos données

Pour exercer ces droits, contactez-nous à : **contact@webysee.com**

Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).`
    },
    {
      icon: Mail,
      title: "7. Cookies",
      content: `Notre site utilise des cookies pour améliorer votre expérience :

**Cookies essentiels** (toujours actifs)
• Fonctionnement technique du site
• Mémorisation de vos préférences

**Cookies analytiques** (avec consentement)
• Mesure d'audience anonymisée
• Amélioration de nos services

**Cookies marketing** (avec consentement)
• Personnalisation des contenus
• Suivi des conversions

Vous pouvez gérer vos préférences de cookies à tout moment via les paramètres de votre navigateur.`
    }
  ];

  return (
    <div className="bg-black min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              className="text-2xl font-semibold tracking-tight text-white hover:text-accent transition-colors duration-300"
            >
              webysee<span className="text-accent">.</span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors duration-300"
            >
              <ArrowLeft size={18} />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[200px]" />
        
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-accent/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-accent" />
            </div>
            <span className="text-accent text-sm font-medium uppercase tracking-wider">RGPD Compliant</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold text-white mb-6">
            Politique de confidentialité
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            Chez Webysee, nous accordons une importance primordiale à la protection de vos données personnelles. 
            Cette politique explique comment nous collectons, utilisons et protégeons vos informations.
          </p>
          <p className="text-white/40 text-sm mt-6">
            Dernière mise à jour : Janvier 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-32">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12">
          <div className="space-y-12">
            {sections.map((section, index) => (
              <div
                key={index}
                className="bg-white/[0.02] border border-white/[0.05] p-8 hover:border-accent/20 transition-colors duration-300"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-accent" />
                  </div>
                  <h2 className="text-xl font-semibold text-white">{section.title}</h2>
                </div>
                <div className="text-white/60 leading-relaxed whitespace-pre-line pl-14">
                  {section.content.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i} className="text-white">{part}</strong> : part
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Box */}
          <div className="mt-16 bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 p-8">
            <h3 className="text-xl font-semibold text-white mb-4">Une question sur vos données ?</h3>
            <p className="text-white/60 mb-6">
              Pour toute question concernant cette politique ou pour exercer vos droits, contactez notre délégué à la protection des données :
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:contact@webysee.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-black font-medium hover:bg-accent/90 transition-colors"
              >
                <Mail size={18} />
                contact@webysee.com
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 text-white border border-white/10 hover:bg-white/10 transition-colors"
              >
                <ArrowLeft size={18} />
                Retour à l'accueil
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.05] py-8">
        <div className="max-w-[900px] mx-auto px-6 lg:px-12 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Webysee. Tous droits réservés.
          </p>
          <div className="flex gap-6">
            <Link to="/mentions-legales" className="text-white/40 hover:text-white text-sm transition-colors">
              Mentions légales
            </Link>
            <Link to="/politique-confidentialite" className="text-accent text-sm">
              Politique de confidentialité
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
